<script setup lang="ts">
// Settings: a menu of sections (Profile, Audio/video, Linked devices with this device's
// notifications, Storage when the server allows it, Migration), each opened in place with
// Back. Where it is lives in the URL (lib/settingsNav.ts); a whole screen on phones.
import { needsInstallForPush } from '../lib/push'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { describeError } from '../api/http'
import { formatSafetyCode } from '../lib/format'
import { useSession } from '../stores/session'
import { useSettingsNav, type SettingsSection } from '../lib/settingsNav'
import SectionMenu from './SectionMenu.vue'
import Modal from './Modal.vue'
import DevicesPanel from './DevicesPanel.vue'
import AvSettings from './AvSettings.vue'
import PixelEditorModal from './PixelEditorModal.vue'
import Avatar from './Avatar.vue'
import DataPanel from './DataPanel.vue'
import MyStorage from './MyStorage.vue'
import { useUnread } from '../stores/unread'
import { setTheme, storedTheme, type ThemeChoice } from '../lib/theme'

const nav = useSettingsNav()
const session = useSession()

const sections = computed(() =>
  (
    [
      ['profile', 'Profile', 'Picture, name, appearance'],
      ['av', 'Audio/video', 'Camera and microphone', session.features.calls],
      ['devices', 'Linked devices', 'Notifications, devices, recovery code'],
      ['storage', 'Your storage', 'Your buckets and nodes, and nodes opened to you', session.canLinkStorage],
      ['migration', 'Migration', 'Export, import, move to another server'],
    ] as [SettingsSection, string, string, boolean?][]
  )
    .filter(([, , , offered]) => offered !== false)
    .map(([id, label, hint]) => ({ id, label, hint })),
)
/** The open section, or null on the menu (a section not offered shows the menu). */
const shown = computed(() => sections.value.find((s) => s.id === nav.at.value) ?? null)
/** The section last open, so the menu puts the focus back on its row. */
const left = ref<string | null>(null)
watch(shown, (now, was) => {
  if (!now && was) left.value = was.id
})
const route = useRoute()
const router = useRouter()
const unread = useUnread()
/** iPhones only offer notifications to installed apps. */
const installFirst = needsInstallForPush()
const themes: ThemeChoice[] = ['dark', 'light', 'system']
const theme = ref<ThemeChoice>(storedTheme())
function pickTheme(t: ThemeChoice): void {
  theme.value = t
  setTheme(t)
}

const profile = computed(() => session.profiles.get(session.me ?? ''))
const name = ref(profile.value?.display_name ?? '')
const saving = ref(false)
const convId = computed(() => (route.params.convId as string | undefined) ?? null)
const safety = computed(() => (convId.value ? session.engine?.safetyCode(convId.value) : null))

async function save(): Promise<void> {
  saving.value = true
  try {
    await session.updateDisplayName(name.value.trim())
    session.toast('Name updated')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    saving.value = false
  }
}

const avatarInput = ref<HTMLInputElement | null>(null)
/** The picture designer replaces this dialog while open (Escape returns here). */
const designing = ref(false)

/** Crops to a centred square and scales to 256px before uploading. */
async function pickAvatar(file: File | undefined): Promise<void> {
  if (!file) return
  try {
    const bmp = await createImageBitmap(file)
    const side = Math.min(bmp.width, bmp.height)
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = 256
    canvas
      .getContext('2d')
      ?.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, 256, 256)
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/webp', 0.85))
    const out = blob?.type === 'image/webp' ? blob : await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.85))
    if (!out) throw new Error('could not process that image')
    await session.api.setAvatar(out)
    await session.refresh()
    session.toast('Profile picture updated')
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function signOut(): Promise<void> {
  if (
    !confirm(
      'Sign out of this device? Its message history will be erased. To use your account here again, link it from another device or use your recovery code.',
    )
  )
    return
  await session.signOut()
  await router.push('/')
}
</script>

<template>
  <PixelEditorModal
    v-if="designing"
    title="Design your picture"
    :initial="profile?.pixels ?? null"
    :seed-id="session.me ?? ''"
    :action="(p) => session.setPixels(p).then(() => session.toast(p ? 'Picture saved' : 'Design removed'))"
    @close="designing = false"
  />
  <Modal
    v-else
    page
    :title="shown?.label ?? 'Settings'"
    :back="shown ? 'Settings' : undefined"
    @back="nav.back()"
    @close="nav.close()"
  >
    <SectionMenu v-if="!shown" :sections="sections" :from="left" testid="settings-section" @open="(id) => nav.enter(id as SettingsSection)" />

    <div
      v-else-if="shown.id === 'profile'"
      id="settings-panel-profile"
      class="space-y-5"
    >
      <div class="flex items-center gap-3">
        <Avatar :id="session.me ?? ''" :name="profile?.display_name ?? '?'" :size="56" />
        <button v-if="session.profilePhotos" class="btn-ghost text-xs" data-testid="change-avatar" @click="avatarInput?.click()">Upload photo</button>
        <button class="btn-ghost text-xs" data-testid="design-avatar" @click="designing = true">Design</button>
        <input
          ref="avatarInput"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          class="hidden"
          data-testid="avatar-input"
          @change="pickAvatar(($event.target as HTMLInputElement).files?.[0])"
        />
      </div>
      <form class="space-y-2" @submit.prevent="save">
        <label class="label" for="settings-name">Display name</label>
        <div class="flex gap-2">
          <input id="settings-name" v-model="name" class="input" maxlength="64" />
          <button class="btn-primary shrink-0" :disabled="saving || !name.trim() || name === profile?.display_name">
            Save
          </button>
        </div>
      </form>
      <div class="space-y-1 text-sm">
        <div class="label">Your ID</div>
        <div class="mono break-all text-text-secondary" data-testid="my-user-id">{{ session.me }}</div>
      </div>
      <div>
        <div class="label">Appearance</div>
        <div class="grid grid-cols-3 border border-border" role="radiogroup" aria-label="Appearance">
          <button
            v-for="t in themes"
            :key="t"
            type="button"
            role="radio"
            class="mono py-1.5 tracking-[0.12em] uppercase transition-colors"
            :class="theme === t ? 'bg-ink text-paper' : 'text-text-muted hover:text-text-primary'"
            :aria-checked="theme === t"
            :data-testid="`theme-${t}`"
            @click="pickTheme(t)"
          >
            {{ t }}
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="shown.id === 'av'" id="settings-panel-av">
      <div class="mono mb-3 tracking-[0.14em] text-text-primary uppercase">Audio &amp; video</div>
      <AvSettings />
    </div>

    <div
      v-else-if="shown.id === 'devices'"
      id="settings-panel-devices"
      class="space-y-5"
    >
      <div class="space-y-1 text-sm">
        <div class="label">This device</div>
        <div class="mono break-all text-text-muted">{{ session.engine?.deviceId }}</div>
      </div>
      <label class="flex items-center justify-between gap-2 text-sm">
        <span>
          Device Notifications
          <span class="block text-xs text-text-muted">
            Turns all notifications on or off, on this device only. To silence one conversation on all your
            devices, open that conversation's settings.
            <template v-if="unread.notifications && !unread.pushing"> Only while hrth is open on this device.</template>
            <template v-else-if="unread.pushing"> Even while hrth is closed.</template>
          </span>
          <span v-if="unread.pushProblem" class="block text-xs text-text-muted" data-testid="notifications-push-problem">
            <template v-if="unread.notifications">Not while hrth is closed: </template>{{ unread.pushProblem }}
          </span>
          <span v-if="installFirst" class="block text-xs text-text-muted" data-testid="notifications-install-hint">
            On iPhone and iPad, add hrth to your Home Screen first (Share → Add to Home Screen), then turn this on there.
          </span>
        </span>
        <input
          type="checkbox"
          :checked="unread.notifications"
          data-testid="toggle-notifications"
          @change="unread.enableNotifications(($event.target as HTMLInputElement).checked)"
        />
      </label>
      <div v-if="safety" class="border border-border p-3">
        <div class="label">Safety code for this conversation</div>
        <div class="mono text-sm tracking-[0.2em] text-text-primary" data-testid="safety-code">{{ formatSafetyCode(safety) }}</div>
        <p class="mt-1 text-xs text-text-muted">
          Compare with a friend in person. If it matches on both devices, nobody — not even the server — is
          intercepting this conversation.
        </p>
      </div>
      <DevicesPanel />
      <div class="mono flex items-center gap-2 text-text-muted">
        <span class="h-1.5 w-1.5" :class="session.socketState === 'open' ? 'bg-ink' : 'animate-blink bg-signal'" />
        LINK <span class="uppercase" :class="session.socketState === 'open' ? 'text-text-primary' : 'text-signal'">{{ session.socketState }}</span>
      </div>
      <button class="btn-danger w-full" data-testid="sign-out" @click="signOut">Sign out of this device</button>
    </div>

    <div v-else-if="shown.id === 'storage'" id="settings-panel-storage">
      <MyStorage />
    </div>

    <div v-else id="settings-panel-migration">
      <DataPanel />
    </div>
  </Modal>
</template>
